import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  ChevronDown,
  FolderTree,
  ImagePlus,
  Layers3,
  Loader2,
  Save,
} from 'lucide-react';
import {
  Link,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  categoriesApi,
  getApiValidationErrors,
  menusApi,
} from '../../api';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import getTranslatedField from '../../i18n/helpers/getTranslatedField';
import translateApiError from '../../utils/translateApiError';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import '../../styles/Categories/categories.css';
import '../../styles/Categories/category-form.css';

const DEFAULT_ESTABLISHMENT_ID = 1;
const SELECTED_MENU_STORAGE_KEY = 'selected_menu_id';

const CATEGORY_LOCALES = ['fr', 'ar', 'en', 'es', 'de', 'zh', 'ru', 'nl', 'ja', 'it', 'pt', 'tr'];

// Accept the menu language selection once returned by the menu API.
// Older menus without an explicit selection continue to support all 12 languages.
const getMenuLocales = (menu) => {
  if (!menu) return CATEGORY_LOCALES;

  const raw = menu.enabled_languages ?? menu.selected_languages ??
    menu.available_languages ?? menu.settings?.enabled_languages ??
    menu.settings?.selected_languages ?? menu.settings?.available_languages;

  if (raw === undefined || raw === null || raw === '') return CATEGORY_LOCALES;

  let values = raw;
  if (typeof values === 'string') {
    try { values = JSON.parse(values); }
    catch { values = values.split(',').map((entry) => entry.trim()); }
  }
  if (values && !Array.isArray(values) && typeof values === 'object') {
    values = Object.keys(values).filter((key) => Boolean(values[key]));
  }
  if (!Array.isArray(values)) return CATEGORY_LOCALES;

  const configured = new Set(values.filter((item) => typeof item === 'string'));
  const defaultLanguage = CATEGORY_LOCALES.includes(menu.default_language)
    ? menu.default_language : 'fr';
  configured.add(defaultLanguage);
  return CATEGORY_LOCALES.filter((locale) => configured.has(locale));
};

const createEmptyTranslations = () =>
  Object.fromEntries(
    CATEGORY_LOCALES.map((locale) => [locale, { name: '', description: '' }]),
  );


function CustomSelect({
  value,
  options = [],
  onChange,
  disabled = false,
  placeholder = '—',
  ariaLabel,
  className = '',
  name,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleOutsideClick,
    );

    return () =>
      document.removeEventListener(
        'mousedown',
        handleOutsideClick,
      );
  }, []);

  const selectedOption =
    options.find(
      (option) =>
        String(option.value) ===
        String(value),
    ) || null;

  return (
    <div
      ref={rootRef}
      className={`category-form-custom-select ${className}`}
    >
      {name ? (
        <input
          type="hidden"
          name={name}
          value={value}
        />
      ) : null}

      <button
        type="button"
        className={`category-form-custom-select__trigger ${
          open ? 'is-open' : ''
        }`}
        onClick={() => {
          if (!disabled) {
            setOpen(
              (current) =>
                !current,
            );
          }
        }}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
      >
        <span>
          {selectedOption?.label ||
            placeholder}
        </span>

        <ChevronDown
          size={17}
          className={
            open
              ? 'is-rotated'
              : ''
          }
        />
      </button>

      {open ? (
        <div
          className="category-form-custom-select__menu"
          role="listbox"
        >
          {options.map(
            (option) => {
              const active =
                String(
                  option.value,
                ) ===
                String(value);

              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={
                    active
                      ? 'is-active'
                      : ''
                  }
                  onClick={() => {
                    onChange?.(
                      option.value,
                    );
                    setOpen(false);
                  }}
                >
                  <span>
                    {option.label}
                  </span>

                  {active ? (
                    <Check
                      size={15}
                    />
                  ) : null}
                </button>
              );
            },
          )}
        </div>
      ) : null}
    </div>
  );
}

function CategoryForm() {
  const { categoryId } = useParams();
  const navigate = useNavigate();

  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();

  const [direction, setDirection] = useState(
    document.documentElement.dir || 'ltr',
  );

  const isRtl = direction === 'rtl';
  const isEditMode = Boolean(categoryId);

  const establishmentId =
    localStorage.getItem(
      'selected_establishment_id',
    ) || DEFAULT_ESTABLISHMENT_ID;

  const [menus, setMenus] = useState([]);
  const [menusLoading, setMenusLoading] =
    useState(true);

  const [
    selectedMenuId,
    setSelectedMenuId,
  ] = useState(
    localStorage.getItem(
      SELECTED_MENU_STORAGE_KEY,
    ) || '',
  );

  const [
    parentCategories,
    setParentCategories,
  ] = useState([]);

  const [
    parentsLoading,
    setParentsLoading,
  ] = useState(false);

  const [form, setForm] = useState({
    parent_id: '',
    sort_order: 0,
    is_active: true,
    regenerate_slug: false,
    translations:
      createEmptyTranslations(),
    image: null,
  });

  const [
    imagePreview,
    setImagePreview,
  ] = useState(null);

  const [
    existingImageUrl,
    setExistingImageUrl,
  ] = useState('');

  const [loading, setLoading] =
    useState(isEditMode);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState('');

  const [
    validationErrors,
    setValidationErrors,
  ] = useState({});

  const selectedMenu = useMemo(
    () =>
      menus.find(
        (menu) =>
          String(menu.id) ===
          String(selectedMenuId),
      ) || null,
    [menus, selectedMenuId],
  );

  const visibleLocales = useMemo(() => getMenuLocales(selectedMenu), [selectedMenu]);

  const pageTitle = isEditMode
    ? t('categories.form.editTitle')
    : t('categories.form.createTitle');

  const pageDescription = isEditMode
    ? t(
        'categories.form.editDescription',
      )
    : t(
        'categories.form.createDescription',
      );

  const selectedName = useMemo(() => {
    return getTranslatedField(
      {
        translations:
          form.translations,
      },
      'name',
      language,
      'fr',
    );
  }, [
    form.translations,
    language,
  ]);

  const selectedParent = useMemo(
    () =>
      parentCategories.find(
        (category) =>
          String(category.id) ===
          String(form.parent_id),
      ),
    [
      parentCategories,
      form.parent_id,
    ],
  );

  const selectedParentName =
    useMemo(
      () =>
        getTranslatedField(
          selectedParent,
          'name',
          language,
          'fr',
        ),
      [
        selectedParent,
        language,
      ],
    );

  const translateValidationMessage =
    (message) => {
      if (!message) {
        return '';
      }

      const normalizedMessage =
        String(message)
          .toLowerCase();

      if (
        normalizedMessage.includes(
          'required',
        )
      ) {
        return t(
          'validation.messages.required',
        );
      }

      if (
        normalizedMessage.includes(
          'image',
        )
      ) {
        return t(
          'validation.messages.image',
        );
      }

      if (
        normalizedMessage.includes(
          'mimes',
        )
      ) {
        return t(
          'validation.messages.mimes',
        );
      }

      if (
        normalizedMessage.includes(
          'dimensions',
        )
      ) {
        return t(
          'validation.messages.dimensions',
        );
      }

      if (
        normalizedMessage.includes(
          'integer',
        )
      ) {
        return t(
          'validation.messages.integer',
        );
      }

      if (
        normalizedMessage.includes(
          'boolean',
        )
      ) {
        return t(
          'validation.messages.boolean',
        );
      }

      return message;
    };

  const getFieldError = (
    field,
  ) => {
    const message =
      validationErrors[field]?.[0] ||
      '';

    return translateValidationMessage(
      message,
    );
  };

  const clearFieldError = (
    field,
  ) => {
    setValidationErrors(
      (currentErrors) => {
        if (!currentErrors[field]) {
          return currentErrors;
        }

        const nextErrors = {
          ...currentErrors,
        };

        delete nextErrors[field];

        return nextErrors;
      },
    );
  };

  const updateField = (
    field,
    value,
  ) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));

    clearFieldError(field);
  };

  const updateTranslation = (
    locale,
    field,
    value,
  ) => {
    setForm((currentForm) => ({
      ...currentForm,
      translations: {
        ...currentForm.translations,
        [locale]: {
          ...(currentForm.translations?.[locale] ?? { name: '', description: '' }),
          [field]: value,
        },
      },
    }));

    clearFieldError(
      `translations.${locale}.${field}`,
    );
  };

  // Keep this page synchronized with the global application direction.
  useEffect(() => {
    const updateDirection = () => {
      setDirection(document.documentElement.dir || 'ltr');
    };

    updateDirection();

    const observer = new MutationObserver(updateDirection);

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['dir', 'lang'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Load all menus of the establishment.
  const fetchMenus = async () => {
    try {
      setMenusLoading(true);

      const response =
        await menusApi.getMenus(
          establishmentId,
        );

      const responseMenus =
        response.data?.data?.menus ||
        response.data?.data ||
        [];

      const normalizedMenus =
        Array.isArray(responseMenus)
          ? responseMenus
          : [];

      setMenus(normalizedMenus);

      if (isEditMode) {
        return;
      }

      const storedMenuId =
        localStorage.getItem(
          SELECTED_MENU_STORAGE_KEY,
        );

      const storedMenuExists =
        normalizedMenus.some(
          (menu) =>
            String(menu.id) ===
            String(storedMenuId),
        );

      if (storedMenuExists) {
        setSelectedMenuId(
          String(storedMenuId),
        );

        return;
      }

      const defaultMenu =
        normalizedMenus.find(
          (menu) =>
            Boolean(
              menu.is_default,
            ),
        ) ||
        normalizedMenus.find(
          (menu) =>
            Boolean(
              menu.is_active,
            ),
        ) ||
        normalizedMenus[0];

      if (defaultMenu?.id) {
        const menuId =
          String(defaultMenu.id);

        setSelectedMenuId(
          menuId,
        );

        localStorage.setItem(
          SELECTED_MENU_STORAGE_KEY,
          menuId,
        );
      } else {
        setSelectedMenuId('');

        localStorage.removeItem(
          SELECTED_MENU_STORAGE_KEY,
        );
      }
    } catch (apiError) {
      const translatedError =
        translateApiError(
          apiError,
          t,
        );

      setError(translatedError);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message:
          translatedError,
      });
    } finally {
      setMenusLoading(false);
    }
  };

  // Load parent categories only from the selected menu.
  const fetchParentCategories =
    async (menuId) => {
      if (!menuId) {
        setParentCategories([]);
        return;
      }

      try {
        setParentsLoading(true);

        const response =
          await categoriesApi
            .getParentCategories(
              establishmentId,
              {
                menu_id:
                  menuId,
              },
            );

        const results =
          response.data?.data ||
          [];

        setParentCategories(
          results.filter(
            (category) =>
              String(category.id) !==
              String(categoryId),
          ),
        );
      } catch {
        setParentCategories(
          [],
        );
      } finally {
        setParentsLoading(false);
      }
    };

  // Load category and lock edit mode to its original menu.
  const fetchCategory =
    async () => {
      if (!isEditMode) {
        return;
      }

      try {
        setLoading(true);
        setError('');

        const response =
          await categoriesApi
            .getCategory(
              categoryId,
            );

        const category =
          response.data?.data
            ?.category ||
          response.data?.data;

        if (!category) {
          throw new Error(
            t(
              'categories.empty.title',
            ),
          );
        }

        const categoryMenuId =
          String(
            category.menu_id ||
              category.menu?.id ||
              '',
          );

        if (
          categoryMenuId
        ) {
          setSelectedMenuId(
            categoryMenuId,
          );

          localStorage.setItem(
            SELECTED_MENU_STORAGE_KEY,
            categoryMenuId,
          );
        }

        setForm({
          parent_id:
            category.parent_id ||
            '',

          sort_order:
            category.sort_order ||
            0,

          is_active:
            Boolean(
              category.is_active,
            ),

          regenerate_slug:
            false,

          translations: {
            ...category.translations,
            ...Object.fromEntries(
              CATEGORY_LOCALES.map((locale) => [
                locale,
                {
                  name: category.translations?.[locale]?.name ?? '',
                  description: category.translations?.[locale]?.description ?? '',
                },
              ]),
            ),
          },

          image: null,
        });

        setExistingImageUrl(
          resolveMediaUrl(
            category.image_url ||
              category.image_path ||
              category.image,
          ) || '',
        );
      } catch (apiError) {
        const translatedError =
          translateApiError(
            apiError,
            t,
          );

        setError(
          translatedError,
        );

        showAlert({
          type: 'error',
          title: t(
            'alerts.error',
          ),
          message:
            translatedError,
        });
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchMenus();
  }, [establishmentId]);

  useEffect(() => {
    fetchCategory();
  }, [categoryId]);

  useEffect(() => {
    if (!selectedMenuId) {
      setParentCategories([]);
      return;
    }

    fetchParentCategories(
      selectedMenuId,
    );
  }, [
    selectedMenuId,
    categoryId,
  ]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(
          imagePreview,
        );
      }
    };
  }, [imagePreview]);

  const handleMenuChange = (
    menuId,
  ) => {
    if (isEditMode) {
      return;
    }

    setSelectedMenuId(menuId);

    updateField(
      'parent_id',
      '',
    );

    if (menuId) {
      localStorage.setItem(
        SELECTED_MENU_STORAGE_KEY,
        menuId,
      );
    } else {
      localStorage.removeItem(
        SELECTED_MENU_STORAGE_KEY,
      );
    }
  };

  const handleImageChange = (
    event,
  ) => {
    const file =
      event.target.files?.[0] ||
      null;

    if (imagePreview) {
      URL.revokeObjectURL(
        imagePreview,
      );
    }

    updateField(
      'image',
      file,
    );

    setImagePreview(
      file
        ? URL.createObjectURL(
            file,
          )
        : null,
    );
  };

  const buildPayload = () => {
    return {
      menu_id:
        selectedMenuId,

      parent_id:
        form.parent_id ||
        null,

      sort_order:
        Number(
          form.sort_order ||
            0,
        ),

      is_active:
        form.is_active
          ? 1
          : 0,

      regenerate_slug:
        form.regenerate_slug
          ? 1
          : 0,

      translations:
        form.translations,

      image:
        form.image,
    };
  };

  const scrollToFirstError = (
    errors,
  ) => {
    const firstField =
      Object.keys(
        errors || {},
      )[0];

    if (!firstField) {
      return;
    }

    const baseField =
      firstField.split('.')[0];

    window.setTimeout(() => {
      const field =
        document.querySelector(
          `[name="${firstField}"]`,
        ) ||
        document.querySelector(
          `[name="${baseField}"]`,
        );

      if (!field) {
        return;
      }

      field.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      field.focus?.();
    }, 50);
  };

  const handleSubmit = async (
    event,
  ) => {
    event.preventDefault();

    if (!selectedMenuId) {
      const menuError =
        t(
          'menus.empty.title',
        );

      setError(menuError);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: menuError,
      });

      return;
    }

    try {
      setSaving(true);
      setError('');
      setValidationErrors({});

      const payload =
        buildPayload();

      if (isEditMode) {
        await categoriesApi
          .updateCategory(
            categoryId,
            payload,
          );

        showAlert({
          type: 'success',
          title: t(
            'alerts.success',
          ),
          message: t(
            'categories.alerts.updated',
          ),
        });
      } else {
        await categoriesApi
          .createCategory(
            establishmentId,
            payload,
          );

        showAlert({
          type: 'success',
          title: t(
            'alerts.success',
          ),
          message: t(
            'categories.alerts.created',
          ),
        });
      }

      navigate(
        '/categories',
      );
    } catch (apiError) {
      const translatedError =
        translateApiError(
          apiError,
          t,
        );

      const errors =
        getApiValidationErrors(
          apiError,
        );

      setError(
        translatedError,
      );

      setValidationErrors(
        errors,
      );

      scrollToFirstError(
        errors,
      );

      showAlert({
        type: 'error',
        title: t(
          'alerts.error',
        ),
        message:
          translatedError,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main
        className={`category-form-page ${
          isRtl
            ? 'category-form-page--rtl'
            : 'category-form-page--ltr'
        }`}
        dir={direction}
      >
        <section className="category-form-loading-panel">
          <div className="category-form-loading">
            <Loader2 size={28} />

            <span>
              {t(
                'categories.loading',
              )}
            </span>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main
      className={`category-form-page ${
        isRtl
          ? 'category-form-page--rtl'
          : 'category-form-page--ltr'
      }`}
      dir={direction}
    >
      <header className="category-form-header">
        <Link
          to="/categories"
          className="category-form-back-button"
        >
          {isRtl ? (
            <ArrowRight size={18} />
          ) : (
            <ArrowLeft size={18} />
          )}

          <span>
            {t(
              'categories.form.backToCategories',
            )}
          </span>
        </Link>

        <div className="category-form-header__copy">
          <p className="category-form-eyebrow">
            {t(
              'categories.eyebrow',
            )}
          </p>

          <h1>{pageTitle}</h1>

          <span>{pageDescription}</span>
        </div>
      </header>

      {error ? (
        <div className="category-form-alert category-form-alert--error">
          {error}
        </div>
      ) : null}

      <form
        className="category-form"
        onSubmit={
          handleSubmit
        }
        noValidate
      >
        <section className="category-form-section category-form-section--main">
          <div className="category-form-section__title">
            <span className="category-form-section__icon">
              <FolderTree size={20} />
            </span>

            <div>
              <h2>
                {t(
                  'categories.form.basicInfo',
                )}
              </h2>

              <p>
                {t(
                  'categories.form.basicInfoDescription',
                )}
              </p>
            </div>
          </div>

          <div className="category-form-menu-context">
            <div className="category-form-menu-context__icon">
              <BookOpen
                size={22}
              />
            </div>

            <div className="category-form-menu-context__content">
              <span>
                {t(
                  'menus.title',
                )}
              </span>

              <strong>
                {selectedMenu
                  ?.name ||
                  '—'}
              </strong>

              <small>
                {isEditMode
                  ? t(
                      'categories.form.editDescription',
                    )
                  : t(
                      'menus.description',
                    )}
              </small>
            </div>

            <CustomSelect
              name="menu_id"
              value={
                selectedMenuId
              }
              onChange={
                handleMenuChange
              }
              disabled={
                menusLoading ||
                isEditMode
              }
              ariaLabel={t(
                'menus.title',
              )}
              className="category-form__menu-select"
              placeholder={
                menusLoading
                  ? t(
                      'menus.loading',
                    )
                  : t(
                      'menus.empty.title',
                    )
              }
              options={menus.map(
                (menu) => ({
                  value:
                    String(
                      menu.id,
                    ),
                  label:
                    `${menu.name}${
                      menu.is_default
                        ? ` • ${t(
                            'menus.status.default',
                          )}`
                        : ''
                    }`,
                }),
              )}
            />
          </div>

          {getFieldError(
            'menu_id',
          ) ? (
            <div className="category-form-alert category-form-alert--error">
              {getFieldError(
                'menu_id',
              )}
            </div>
          ) : null}

          <div className="category-form-grid category-form-grid--main">
            <label className="category-form__field">
              <span>
                {t(
                  'categories.form.parentCategory',
                )}
              </span>

              <CustomSelect
                name="parent_id"
                value={
                  form.parent_id
                }
                onChange={(
                  value,
                ) =>
                  updateField(
                    'parent_id',
                    value,
                  )
                }
                disabled={
                  !selectedMenuId ||
                  parentsLoading
                }
                ariaLabel={t(
                  'categories.form.parentCategory',
                )}
                placeholder={
                  parentsLoading
                    ? t(
                        'categories.loading',
                      )
                    : t(
                        'categories.form.noParentCategory',
                      )
                }
                options={[
                  {
                    value: '',
                    label:
                      parentsLoading
                        ? t(
                            'categories.loading',
                          )
                        : t(
                            'categories.form.noParentCategory',
                          ),
                  },

                  ...parentCategories.map(
                    (
                      category,
                    ) => ({
                      value:
                        String(
                          category.id,
                        ),
                      label:
                        getTranslatedField(
                          category,
                          'name',
                          language,
                          'fr',
                        ) ||
                        t(
                          'categories.noName',
                        ),
                    }),
                  ),
                ]}
              />

              <small>
                {form.parent_id
                  ? t(
                      'categories.form.subcategoryHelp',
                    )
                  : t(
                      'categories.form.mainCategoryHelp',
                    )}
              </small>

              {getFieldError(
                'parent_id',
              ) ? (
                <em>
                  {getFieldError(
                    'parent_id',
                  )}
                </em>
              ) : null}
            </label>

            <label className="category-form__field">
              <span>
                {t(
                  'categories.form.sortOrder',
                )}
              </span>

              <input
                name="sort_order"
                type="number"
                min="0"
                value={
                  form.sort_order
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    'sort_order',
                    event.target
                      .value,
                  )
                }
              />

              {getFieldError(
                'sort_order',
              ) ? (
                <em>
                  {getFieldError(
                    'sort_order',
                  )}
                </em>
              ) : null}
            </label>

            <label className="category-form__checkbox">
              <input
                name="is_active"
                type="checkbox"
                checked={
                  form.is_active
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    'is_active',
                    event.target
                      .checked,
                  )
                }
              />

              <span>
                {t(
                  'categories.form.isActive',
                )}
              </span>
            </label>

            {isEditMode ? (
              <label className="category-form__checkbox">
                <input
                  name="regenerate_slug"
                  type="checkbox"
                  checked={
                    form.regenerate_slug
                  }
                  onChange={(
                    event,
                  ) =>
                    updateField(
                      'regenerate_slug',
                      event.target
                        .checked,
                    )
                  }
                />

                <span>
                  {t(
                    'categories.form.regenerateSlug',
                  )}
                </span>
              </label>
            ) : null}
          </div>

          {selectedName ||
          selectedParentName ? (
            <div className="category-form__preview-name">
              {form.parent_id ? (
                <Layers3
                  size={18}
                />
              ) : (
                <FolderTree
                  size={18}
                />
              )}

              <span>
                {form.parent_id
                  ? `${selectedParentName || '-'} / ${
                      selectedName ||
                      t(
                        'categories.noName',
                      )
                    }`
                  : selectedName}
              </span>
            </div>
          ) : null}
        </section>

        <section className="category-form-section">
          <div className="category-form-section__title">
            <span className="category-form-section__icon">
              <Layers3 size={20} />
            </span>

            <div>
              <h2>
                {t(
                  'categories.form.translations',
                )}
              </h2>

              <p>
                {t(
                  'categories.form.translationsDescription',
                )}
              </p>
            </div>
          </div>

          <p className="category-form__language-summary" aria-live="polite">
              {selectedMenu
                ? `${visibleLocales.length} / ${CATEGORY_LOCALES.length} ${t('header.language')}`
                : t('menus.empty.title')}
            </p>
            <div className="category-form__translations">
            {visibleLocales.map(
              (locale) => (
                <div
                  className="category-form__translation-card"
                  key={
                    locale
                  }
                >
                  <h3>
                    {t(
                      `languages.${locale}`,
                    )}
                  </h3>

                  <label className="category-form__field">
                    <span>
                      {t(
                        'categories.form.name',
                      )}
                    </span>

                    <input
                      name={`translations.${locale}.name`}
                      type="text"
                      value={
                        form.translations?.[locale]?.name ?? ''
                      }
                      onChange={(
                        event,
                      ) =>
                        updateTranslation(
                          locale,
                          'name',
                          event
                            .target
                            .value,
                        )
                      }
                      dir={
                        locale ===
                        'ar'
                          ? 'rtl'
                          : 'ltr'
                      }
                    />

                    {getFieldError(
                      `translations.${locale}.name`,
                    ) ? (
                      <em>
                        {getFieldError(
                          `translations.${locale}.name`,
                        )}
                      </em>
                    ) : null}
                  </label>

                  <label className="category-form__field">
                    <span>
                      {t(
                        'categories.form.description',
                      )}
                    </span>

                    <textarea
                      name={`translations.${locale}.description`}
                      value={
                        form.translations?.[locale]?.description ?? ''
                      }
                      onChange={(
                        event,
                      ) =>
                        updateTranslation(
                          locale,
                          'description',
                          event
                            .target
                            .value,
                        )
                      }
                      dir={
                        locale ===
                        'ar'
                          ? 'rtl'
                          : 'ltr'
                      }
                    />

                    {getFieldError(
                      `translations.${locale}.description`,
                    ) ? (
                      <em>
                        {getFieldError(
                          `translations.${locale}.description`,
                        )}
                      </em>
                    ) : null}
                  </label>
                </div>
              ),
            )}
          </div>
        </section>

        <section className="category-form-section">
          <div className="category-form-section__title">
            <span className="category-form-section__icon">
              <ImagePlus size={20} />
            </span>

            <div>
              <h2>
                {t(
                  'categories.form.image',
                )}
              </h2>

              <p>
                {t(
                  'categories.form.imageDescription',
                )}
              </p>
            </div>
          </div>

          <label className="category-form__upload">
            <ImagePlus
              size={28}
            />

            <strong>
              {t(
                'categories.form.uploadImage',
              )}
            </strong>

            <span>
              {t(
                'categories.form.uploadHelp',
              )}
            </span>

            <input
              name="image"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={
                handleImageChange
              }
            />
          </label>

          {getFieldError(
            'image',
          ) ? (
            <div className="category-form-alert category-form-alert--error">
              {getFieldError(
                'image',
              )}
            </div>
          ) : null}

          {imagePreview ||
          existingImageUrl ? (
            <div className="category-form__image-preview">
              <img
                src={
                  imagePreview ||
                  existingImageUrl
                }
                alt={
                  selectedName ||
                  t('categories.noName')
                }
              />
            </div>
          ) : null}
        </section>

        <div className="category-form__actions">
          <Link
            to="/categories"
            className="dashboard-secondary-button"
          >
            {t(
              'common.cancel',
            )}
          </Link>

          <button
            type="submit"
            className="dashboard-primary-button"
            disabled={
              saving ||
              !selectedMenuId
            }
          >
            {saving ? (
              <Loader2
                size={18}
                className="menu-form-spinner"
              />
            ) : (
              <Save
                size={18}
              />
            )}

            {isEditMode
              ? t(
                  'categories.form.update',
                )
              : t(
                  'categories.form.save',
                )}
          </button>
        </div>
      </form>
    </main>
  );
}

export default CategoryForm;